import {
  FormDatePicker,
  FormFileInput,
  FormInput,
  FormSelect,
  FormTextarea,
} from "@/components";
import { Button } from "@/components/ui";
import { LOGIN_ROLES } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { Bookmark, Download } from "lucide-react";
import { FC, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  BANK_ACCOUNT_DETAILS,
  EXPECTED_INVOICE_CLEARANCE_DATE,
  EXPECTED_INVOICE_GENERATION_DATE,
  EXPECTED_INVOICE_SUBMISSION_DATE,
  INVOICE_CODE,
  INVOICE_FILE,
  INVOICE_GENERATION_DATE,
  INVOICE_OWNER,
  INVOICE_OWNER_ROLE,
  INVOICE_REMARKS,
  INVOICE_SUBMISSION_DATE,
} from "./consts";
import useInstallmentInvoice from "./useInstallmentInvoice";

const InstallmentInvoice: FC = () => {
  const { id, installmentIndex } = useParams();
  const {
    form,
    requiredFields,
    onSubmit,
    invoiceCodes,
    invoiceOwnerOptions,
    invoiceOwnerValue,
    invoiceCodeOptions,
    attachmentRef,
    isLoading,
    donwloadInvoice,
  } = useInstallmentInvoice();

  const { errors } = form.formState;

  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );

  // 👇 backend sends role as array, e.g. ["CRM_HEAD"]
  const roles = loggedInUserData?.role || [];

  // Helper to check if user has a given role
  const hasRole = (role: string) => Array.isArray(roles) && roles.includes(role);

  // Build stable keys so Select remounts when either options or selected value changes
  const ownerKey = useMemo(
    () =>
      `owner-${invoiceOwnerValue ?? "none"}-opts-${(invoiceOwnerOptions || []).length}`,
    [invoiceOwnerValue, invoiceOwnerOptions]
  );

  const codeKey = useMemo(
    () => `code-${form.watch(INVOICE_CODE) ?? "none"}-opts-${(invoiceCodeOptions || []).length}`,
    [form.watch(INVOICE_CODE), invoiceCodeOptions]
  );

  return (
    <div>
      {/* Header with breadcrumbs */}
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-primary">
                Invoice Lifecycle
              </h2>
              <div className="flex items-center space-x-2">
                <Button
                  type="button"
                  variant="link"
                  className="text-blue-600"
                  onClick={donwloadInvoice}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Invoice
                </Button>
                {(hasRole(LOGIN_ROLES.FINANCE_TEAM) ||
                  hasRole(LOGIN_ROLES.SUPER_ADMIN)) && (
                    <Button type="submit" variant="default" disabled={isLoading}>
                      Save
                    </Button>
                  )}
              </div>
            </div>
            <fieldset
              disabled={
                !(
                  hasRole(LOGIN_ROLES.FINANCE_TEAM) ||
                  hasRole(LOGIN_ROLES.SUPER_ADMIN)
                )
              }
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <FormInput
                  name={EXPECTED_INVOICE_GENERATION_DATE}
                  control={form.control}
                  label="Expected Invoice Generation Date"
                  placeholder="Enter"
                  error={errors.expectedInvoiceGenerationDate?.message}
                  disabled
                />
                <FormInput
                  name={EXPECTED_INVOICE_SUBMISSION_DATE}
                  control={form.control}
                  label="Expected Invoice Submission Date"
                  placeholder="Enter"
                  error={errors.expectedInvoiceSubmissionDate?.message}
                  disabled
                />
                <FormInput
                  name={EXPECTED_INVOICE_CLEARANCE_DATE}
                  control={form.control}
                  label="Expected Invoice Clearance Date"
                  placeholder="Enter"
                  error={errors.expectedInvoiceClearanceDate?.message}
                  disabled
                />
                <FormInput
                  name={INVOICE_GENERATION_DATE}
                  control={form.control}
                  label="Invoice Generation Date"
                  placeholder="Enter"
                  error={errors.invoiceGenerationDate?.message}
                  disabled
                />

                {/* Invoice Code Select - use computed invoiceCodeOptions from hook and a key */}
                <div key={codeKey}>
                  <FormSelect
                    name={INVOICE_CODE}
                    control={form.control}
                    label="Invoice Code"
                    placeholder="Please Select"
                    error={errors.invoiceCode?.message}
                    isRequired={requiredFields.includes("invoiceCode")}
                    options={invoiceCodeOptions ?? invoiceCodes?.data?.map((code: any) => ({
                      label: code.name,
                      value: code._id,
                    }))}
                  />
                </div>

                <FormFileInput
                  fileInputRef={attachmentRef}
                  name={INVOICE_FILE}
                  control={form.control}
                  label="Invoice"
                  placeholder="Upload invoice"
                  error={errors.invoiceFile?.message}
                  accept="application/pdf"
                  disabled={
                    !hasRole(LOGIN_ROLES.FINANCE_TEAM) &&
                    !hasRole(LOGIN_ROLES.SUPER_ADMIN)
                  }
                />

                <FormSelect
                  name={INVOICE_OWNER_ROLE}
                  control={form.control}
                  label="Invoice Owner Role"
                  placeholder="Please Select"
                  error={errors.invoiceOwnerRole?.message}
                  isRequired={requiredFields.includes("invoiceOwnerRole")}
                  options={[
                    { label: "Sales-Ops", value: "SALES_OPS" },
                    { label: "CRM-Head", value: "CRM_HEAD" },
                  ]}
                  disabled={
                    !(
                      hasRole(LOGIN_ROLES.FINANCE_TEAM) ||
                      hasRole(LOGIN_ROLES.SUPER_ADMIN)
                    )
                  }
                />

                {/* Invoice Owner Select - wrap in keyed div so it remounts when value/options change */}
                <div key={ownerKey}>
                  <FormSelect
                    name={INVOICE_OWNER}
                    control={form.control}
                    label="Invoice Owner"
                    placeholder="Please Select"
                    error={errors.invoiceOwner?.message}
                    isRequired={requiredFields.includes("invoiceOwner")}
                    options={invoiceOwnerOptions}
                  />
                </div>

                <FormDatePicker
                  name={INVOICE_SUBMISSION_DATE}
                  control={form.control}
                  label="Invoice Submission Date"
                  placeholder="Please enter"
                  error={errors.invoiceSubmissionDate?.message}
                />
              </div>
              <div className="mt-6">
                <FormTextarea
                  name={BANK_ACCOUNT_DETAILS}
                  control={form.control}
                  label="Bank Account Details"
                  placeholder="Enter"
                  error={errors[BANK_ACCOUNT_DETAILS]?.message}
                />
              </div>
              <div className="mt-6">
                <FormTextarea
                  name={INVOICE_REMARKS}
                  control={form.control}
                  label="Invoice Remarks"
                  placeholder="Please enter"
                  error={errors.invoiceRemark?.message}
                  isRequired={requiredFields.includes("invoiceRemark")}
                  rows={3}
                />
              </div>
            </fieldset>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default InstallmentInvoice;
